import {Button} from 'platform/components';
import {HStack, Show} from 'platform/foundation';

import {useDispatch} from 'react-redux';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {metadaApi} from '@dms/api/base';
import {
  Payment,
  PaymentAllowedAction,
  usePostCheckoutPaymentInvoiceCancelMutation,
  usePostCheckoutPaymentFullCorrectionCreateMutation,
  usePostCheckoutPaymentPartialCorrectionCreateMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useInvalidateServiceOrderJobs} from '../hooks/useInvalidateServiceOrderJobs';

interface CheckoutPaymentInvoiceActionsProps extends RequiredTestIdProps {
  allowedActions: PaymentAllowedAction[];
  checkoutId: string;
  payment: Payment;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  serviceOrderId?: string;
}

export function CheckoutPaymentInvoiceActions(props: CheckoutPaymentInvoiceActionsProps) {
  const dispatch = useDispatch();
  const invalidateServiceOrderJobs = useInvalidateServiceOrderJobs(props.serviceOrderId);

  const [postCheckoutPaymentCancel, {isLoading: isCancelLoading}] =
    usePostCheckoutPaymentInvoiceCancelMutation();
  const [postCheckoutPaymentFullCorrectionCreate, {isLoading: isFullCorrectionLoading}] =
    usePostCheckoutPaymentFullCorrectionCreateMutation();
  const [postCheckoutPaymentPartialCorrectionCreate, {isLoading: isPartialCorrectionLoading}] =
    usePostCheckoutPaymentPartialCorrectionCreateMutation();

  const refreshServiceOrder = () => {
    if (isNotNilOrEmpty(props.serviceOrderId)) {
      dispatch(metadaApi.util.invalidateTags([{type: 'serviceOrder', id: props.serviceOrderId}]));
    }
  };

  const canCancelInvoice = props.allowedActions.includes('CANCEL_INVOICE');
  const canInitializeFullCorrection = props.allowedActions.includes('INITIALIZE_FULL_CORRECTION');
  const canInitializePartialCorrection = props.allowedActions.includes(
    'INITIALIZE_PARTIAL_CORRECTION'
  );

  const isAnyActionLoading =
    isCancelLoading || isFullCorrectionLoading || isPartialCorrectionLoading;

  const isDisabled = props.isEditingDisabled || props.isCompletingDisabled || isAnyActionLoading;

  const handleCancelInvoice = () => {
    postCheckoutPaymentCancel({
      checkoutId: props.checkoutId,
      paymentId: props.payment.checkoutPaymentId,
    })
      .unwrap()
      .then(invalidateServiceOrderJobs)
      .catch(handleApiError);
  };

  const handleInitializeFullCorrection = () => {
    postCheckoutPaymentFullCorrectionCreate({
      checkoutId: props.checkoutId,
      paymentId: props.payment.checkoutPaymentId,
    })
      .unwrap()
      .then(refreshServiceOrder)
      .catch(handleApiError);
  };

  const handleInitializePartialCorrection = () => {
    postCheckoutPaymentPartialCorrectionCreate({
      checkoutId: props.checkoutId,
      paymentId: props.payment.checkoutPaymentId,
    })
      .unwrap()
      .then(refreshServiceOrder)
      .then(invalidateServiceOrderJobs)
      .catch(handleApiError);
  };

  return (
    <HStack spacing={4} align="center">
      <Show when={canCancelInvoice}>
        <Button
          variant="dangerLink"
          size="small"
          title={i18n.t('entity.invoice.actions.cancelDocument')}
          onClick={handleCancelInvoice}
          isDisabled={isDisabled}
          isLoading={isCancelLoading}
          data-testid={suffixTestId('cancelInvoice', props)}
        />
      </Show>
      <Show when={canInitializePartialCorrection}>
        <Button
          variant="link"
          size="default"
          title={i18n.t('entity.accounting.labels.correctiveTaxDocumentPartial')}
          onClick={handleInitializePartialCorrection}
          isDisabled={isDisabled}
          isLoading={isPartialCorrectionLoading}
          data-testid={suffixTestId('initializePartialCorrection', props)}
        />
      </Show>
      <Show when={canInitializeFullCorrection}>
        <Button
          variant="link"
          size="default"
          title={i18n.t('entity.accounting.labels.correctiveTaxDocumentFull')}
          onClick={handleInitializeFullCorrection}
          isDisabled={isDisabled}
          isLoading={isFullCorrectionLoading}
          data-testid={suffixTestId('initializeFullCorrection', props)}
        />
      </Show>
    </HStack>
  );
}
